/**
 * Login page.
 */
import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import toast from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { useAuth } from '@/providers/auth-provider'

const REMEMBER_KEY = 'eco-remember'

export default function LoginPage() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  // Pre-fill username + remember flag from localStorage so returning users
  // only need to type the password.
  const [username, setUsername] = useState(() => {
    try {
      const raw = localStorage.getItem(REMEMBER_KEY)
      return raw ? (JSON.parse(raw) as { username?: string }).username ?? '' : ''
    } catch {
      return ''
    }
  })
  const [remember, setRemember] = useState(() => {
    try {
      const raw = localStorage.getItem(REMEMBER_KEY)
      return raw ? Boolean((JSON.parse(raw) as { remember?: boolean }).remember) : false
    } catch {
      return false
    }
  })
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setIsLoading(true)
    try {
      await login(username, password)
      // Persist remember-me preference. We only store the username (never the
      // password) — even if localStorage is exfiltrated, an attacker still
      // needs the password to log in.
      try {
        if (remember) {
          localStorage.setItem(REMEMBER_KEY, JSON.stringify({ username, remember: true }))
        } else {
          localStorage.removeItem(REMEMBER_KEY)
        }
      } catch {
        // localStorage may be disabled (private mode) — non-fatal
      }
      const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? '/dashboard'
      navigate(from, { replace: true })
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
        'Sai tên đăng nhập hoặc mật khẩu'
      toast.error(msg)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex h-full items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-slate-100 dark:from-emerald-950/30 dark:via-slate-950 dark:to-slate-900">
      <Card className="w-[min(92vw,400px)] border-emerald-200/60 shadow-lg shadow-emerald-900/5 dark:border-emerald-900/40">
        <CardHeader>
          <div className="flex items-center gap-3">
            {/* Logo on light card: multiply blend keeps the green/black, hides
                the white parts of the source PNG so the roof + 'E' stroke are
                not lost against the card background. */}
            <img
              src="/logo.svg"
              alt="Company Monitor"
              width={48}
              height={48}
              className="h-12 w-12 rounded-lg mix-blend-multiply"
            />
            <div>
              <CardTitle className="text-xl">Company Monitor</CardTitle>
              <CardDescription>Giám sát nhân viên thời gian thực</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Tên đăng nhập</Label>
              <Input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
                autoComplete="username"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Mật khẩu</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
                  aria-pressed={showPassword}
                  tabIndex={-1}
                  className="absolute inset-y-0 right-0 flex w-9 items-center justify-center text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:text-foreground"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" aria-hidden />
                  ) : (
                    <Eye className="h-4 w-4" aria-hidden />
                  )}
                </button>
              </div>
            </div>
            <label className="flex cursor-pointer select-none items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-primary accent-primary focus:ring-2 focus:ring-primary/30"
              />
              <span>Ghi nhớ đăng nhập</span>
            </label>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? <Spinner /> : 'Đăng nhập'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
